/**
 * *Who can see "<layer>"* — `docs/property-maps-v2.md` §5.5, board 4.
 *
 * The three options, the two switches, the fixed *Customer portal — Never*
 * row, and the **Who sees it today** card that names the crews the current
 * choice resolves to. That card is the point of the dialog: *Specific
 * divisions* is an abstraction and "4 Snow crews — Okafor, Ramirez, Baker,
 * Hines" is a fact, and somebody about to narrow a layer should see the
 * second before pressing Save.
 *
 * **It previews the choice, not the stored value**, so the names change as
 * the ticks do. §4.4's `audience-preview` takes the audience as a parameter
 * for exactly that reason.
 *
 * **The portal row has no control.** Maps are internal (§3.4), and a switch
 * that can only ever be off is a switch somebody will one day try to turn on.
 */
import { useEffect, useState } from 'react'
import type { Audience } from '@rowted/shared'
import { Button, Switch } from '../../components/primitives.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { cn } from '../../lib/cn.js'
import { IconLock } from '../../frame/icons.js'
import { audiencePreview, type MapLayer } from '../api.js'
import { AudienceField, audienceReady } from './audience-field.js'
import type { Division } from './audience-chip.js'

export function LayerSharingDialog({
  layer, divisions, onCancel, onSave,
}: {
  layer: MapLayer
  divisions: Division[]
  onCancel: () => void
  onSave: (patch: {
    audience: Audience; divisionIds: string[]
    pinnedToTickets: boolean; crewCanAdd: boolean
  }) => Promise<void>
}) {
  const [audience, setAudience] = useState<Audience>(layer.audience)
  const [divisionIds, setDivisionIds] = useState<string[]>(layer.divisionIds)
  const [pinned, setPinned] = useState(layer.pinnedToTickets)
  const [crewCanAdd, setCrewCanAdd] = useState(layer.crewCanAdd)
  const [crews, setCrews] = useState<{ id: string; name: string; divisionName: string | null }[]>([])
  const [busy, setBusy] = useState(false)

  /* Who the *current choice* reaches, re-asked as it changes. */
  useEffect(() => {
    let alive = true
    audiencePreview(layer.id, audience, divisionIds)
      .then((body) => { if (alive) setCrews(body.crews) })
      .catch(() => { if (alive) setCrews([]) })
    return () => { alive = false }
  }, [layer.id, audience, divisionIds])

  const ready = audienceReady(audience, divisionIds)

  return (
    <SheetOrDialog
      onClose={onCancel}
      title={`Who can see “${layer.name}”`}
      width={520}
      footerNote={`Owned by this map · edits are logged in History`}
      footer={(
        <>
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ready || busy}
            onClick={() => {
              setBusy(true)
              void onSave({ audience, divisionIds, pinnedToTickets: pinned, crewCanAdd })
                .finally(() => setBusy(false))
            }}
          >
            {busy ? 'Saving…' : saveWords(audience, crews.length)}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-4 px-5 py-4">
        <AudienceField
          audience={audience}
          divisionIds={divisionIds}
          divisions={divisions}
          onChange={(next) => { setAudience(next.audience); setDivisionIds(next.divisionIds) }}
        />

        <div className="flex flex-col gap-2.5 border-t border-line-faint pt-3.5">
          <Switch
            checked={pinned}
            onChange={setPinned}
            label="Pin to visits here"
          />
          <p className="m-0 pl-11 text-cell text-text-muted">
            Opens by itself on the crew’s visit for this property.
          </p>
          <Switch
            checked={crewCanAdd}
            onChange={setCrewCanAdd}
            label="Crew leads can add marks"
          />
          <p className="m-0 pl-11 text-cell text-text-muted">
            Obstacles and notes only, on their own visits.
          </p>
        </div>

        <div className={cn(
          'flex items-center gap-2 rounded-field border border-line bg-surface-alt px-3 py-2.5',
          'text-cell text-text-muted',
        )}
        >
          <IconLock size={13} />
          <b className="text-text-body">Customer portal</b>
          <span className="ml-auto font-semibold">Never</span>
        </div>

        <div className="rounded-field border border-line bg-surface-alt px-3 py-2.5">
          <b className="text-cell">Who sees it today</b>
          <p className="m-0 mt-1 text-cell text-text-muted">
            {audience === 'me'
              ? 'Only you. It never reaches a visit or the crew app.'
              : crews.length === 0
                ? 'No crews yet — nobody is in these divisions.'
                : `${crews.length} ${crews.length === 1 ? 'crew' : 'crews'} — `
                  + crews.slice(0, 6).map((one) => one.name).join(', ')
                  + (crews.length > 6 ? ` +${crews.length - 6}` : '')}
          </p>
          <p className="m-0 mt-1.5 text-sub text-text-faint">
            Office users with Properties access in this branch always see shared layers.
          </p>
        </div>
      </div>
    </SheetOrDialog>
  )
}

/** `Save · 4 Snow crews` — the count the choice would reach (§5.5). */
function saveWords(audience: Audience, crews: number): string {
  if (audience === 'me') return 'Save · only me'
  if (audience === 'all') return 'Save · all crews'
  return crews === 0 ? 'Save' : `Save · ${crews} ${crews === 1 ? 'crew' : 'crews'}`
}
